<div class="flex w-full min-w-0 flex-col">
  <div mat-dialog-title>
    <div class="flex items-center justify-between gap-4">
      <h1 class="m-0">Call outcome</h1>
      <button mat-icon-button type="button" aria-label="Close dialog" [disabled]="isSaving()" (click)="close()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <div class="flex w-full min-w-0 flex-col gap-4 px-6 pb-4 pt-2">
    <div class="flex w-full min-w-0 gap-4">
      <ng-container [ngTemplateOutlet]="expertDetails"></ng-container>
      <ng-container [ngTemplateOutlet]="opportunityDetails"></ng-container>
    </div>

    <ng-container [ngTemplateOutlet]="notes"></ng-container>

    <mat-divider></mat-divider>

    <ng-container [ngTemplateOutlet]="callForm"></ng-container>
  </div>

  <div mat-dialog-actions>
    <div class="flex flex-1 justify-end gap-2">
      <button
        mat-flat-button
        color="primary"
        type="button"
        data-test-id="call-solicitation-save"
        [disabled]="isSaving() || form.invalid"
        (click)="save()"
      >
        Save outcome
      </button>
    </div>
  </div>
</div>

<ng-template #expertDetails>
  <section class="min-w-0 flex-1 basis-0 rounded border border-gray-200 p-4">
    <h2 class="mb-4 text-base font-medium text-gray-800">Expert details</h2>
    <dl class="flex flex-col gap-4">
      <div class="min-w-0">
        <dt class="text-xs font-medium text-gray-500">Name</dt>
        <dd class="mt-1 min-w-0 break-words text-sm text-gray-900">
          {{ data.expert.name }}
        </dd>
      </div>

      <div class="min-w-0">
        <dt class="text-xs font-medium text-gray-500">Phone</dt>
        <dd class="mt-1 min-w-0 break-words text-sm text-gray-900">
          {{ data.expert.primaryPhoneNumber }}
        </dd>
      </div>

      @if (data.expert.primaryEmail) {
        <div class="min-w-0">
          <dt class="text-xs font-medium text-gray-500">Email</dt>
          <dd class="mt-1 min-w-0 break-words text-sm text-gray-900">
            {{ data.expert.primaryEmail }}
          </dd>
        </div>
      }

      @if (data.expert.linkedin) {
        <div class="min-w-0">
          <dt class="text-xs font-medium text-gray-500">LinkedIn</dt>
          <dd class="mt-1 min-w-0 break-words text-sm text-gray-900">
            <a
              class="text-primary-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
              [href]="data.expert.linkedin"
            >
              {{ data.expert.linkedin }}
            </a>
          </dd>
        </div>
      }

      @if (data.expert.country) {
        <div class="min-w-0">
          <dt class="text-xs font-medium text-gray-500">Country</dt>
          <dd class="mt-1 min-w-0 break-words text-sm text-gray-900">
            {{ data.expert.country }}
          </dd>
        </div>
      }

      @if (data.expert.honoraria; as honoraria) {
        <div class="min-w-0">
          <dt class="text-xs font-medium text-gray-500">Honoraria</dt>
          <dd class="mt-1 min-w-0 break-words text-sm text-gray-900">
            {{ data.opportunity.projectType }}:
            {{
              (data.opportunity.projectType === 'Qual' ? honoraria.qual : honoraria.quant)
                | currency: honoraria.currency
            }}
          </dd>
        </div>
      }

      @if (data.expert.latestJob; as latestJob) {
        <div class="min-w-0">
          <dt class="text-xs font-medium text-gray-500">Role</dt>
          <dd class="mt-1 min-w-0 break-words text-sm text-gray-900">
            {{ latestJob.jobTitle }} at {{ latestJob.company }}
            @if (latestJob.industry) {
              <span class="text-gray-500">({{ latestJob.industry }})</span>
            }
          </dd>
        </div>
      }
    </dl>
  </section>
</ng-template>

<ng-template #opportunityDetails>
  <section class="min-w-0 flex-1 basis-0 rounded border border-gray-200 p-4">
    <h2 class="mb-4 text-base font-medium text-gray-800">Opportunity details</h2>
    <dl class="flex flex-col gap-4">
      <div class="min-w-0">
        <dt class="text-xs font-medium text-gray-500">Opportunity</dt>
        <dd class="mt-1 min-w-0 break-words text-sm text-gray-900">
          {{ data.opportunity.opportunityName }}
        </dd>
      </div>

      <div class="min-w-0">
        <dt class="text-xs font-medium text-gray-500">Type</dt>
        <dd class="mt-1 text-sm text-gray-900">{{ data.opportunity.projectType }}</dd>
      </div>

      @if (data.segment) {
        <div class="min-w-0">
          <dt class="text-xs font-medium text-gray-500">Segment</dt>
          <dd class="mt-1 min-w-0 break-words text-sm text-gray-900">
            {{ data.segment.geography }} &middot; {{ data.segment.segment }}
          </dd>
        </div>
      }

      @if (data.opportunity.anticipatedCallTime) {
        <div class="min-w-0">
          <dt class="text-xs font-medium text-gray-500">Call time</dt>
          <dd class="mt-1 text-sm text-gray-900">{{ data.opportunity.anticipatedCallTime }} minutes</dd>
        </div>
      }
    </dl>
  </section>
</ng-template>

<ng-template #notes>
  @if (data.pinnedNotes.length) {
    <section class="min-w-0 rounded border border-gray-200 p-4">
      <h2 class="mb-3 text-base font-medium text-gray-800">Pinned notes</h2>
      <ul class="flex flex-col gap-2 text-sm">
        @for (note of data.pinnedNotes; track note) {
          <li class="whitespace-pre-wrap break-words rounded bg-gray-50 p-3 text-gray-900">{{ note }}</li>
        }
      </ul>
    </section>
  }
</ng-template>

<ng-template #callForm>
  <form class="w-full min-w-0" [formGroup]="form">
    <mat-form-field class="w-full">
      <mat-label>Call notes</mat-label>
      <textarea
        matInput
        class="!resize-none"
        formControlName="notes"
        rows="10"
        data-test-id="call-solicitation-notes"
      ></textarea>
    </mat-form-field>

    <div class="mt-4 flex items-center gap-4">
      @if (data.expert.honoraria; as honoraria) {
        <mat-form-field class="w-full max-w-36">
          <mat-label>Currency</mat-label>
          <mat-select formControlName="honorariumCurrency" data-test-id="call-solicitation-honorarium-currency">
            @for (currency of currencies; track currency) {
              <mat-option [value]="currency">{{ currency }}</mat-option>
            }
          </mat-select>
          @if (form.controls.honorariumCurrency.hasError('required')) {
            <mat-error>Currency is required</mat-error>
          }
        </mat-form-field>
        <mat-form-field class="w-full max-w-48">
          <mat-label>Qual honorarium</mat-label>
          <input
            matInput
            type="number"
            min="0"
            formControlName="honorariumQual"
            data-test-id="call-solicitation-honorarium-qual"
          />
        </mat-form-field>
        <mat-form-field class="w-full max-w-48">
          <mat-label>Quant honorarium</mat-label>
          <input
            matInput
            type="number"
            min="0"
            formControlName="honorariumQuant"
            data-test-id="call-solicitation-honorarium-quant"
          />
        </mat-form-field>
      }

      <mat-checkbox
        class="ml-auto"
        formControlName="saveNoteToProfile"
        data-test-id="call-solicitation-save-note-to-profile"
      >
        Save note to expert profile
      </mat-checkbox>
    </div>

    <div class="mt-4 flex items-center gap-4">
      <mat-form-field class="w-full max-w-96">
        <mat-label>Outcome</mat-label>
        <mat-select formControlName="outcome" data-test-id="call-solicitation-outcome">
          <mat-option [value]="null">No outcome selected</mat-option>
          @for (option of outcomeOptions; track option) {
            <mat-option [value]="option">{{ option | callSolicitationOutcome }}</mat-option>
          }
        </mat-select>
        @if (form.controls.outcome.hasError('required')) {
          <mat-error>Outcome is required</mat-error>
        }
      </mat-form-field>

      @if (isRejectionOutcome(form.controls.outcome.value)) {
        <mat-form-field class="w-full max-w-96">
          <mat-label>Rejection code</mat-label>
          <mat-select formControlName="rejectionCode" data-test-id="call-solicitation-rejection-code">
            <mat-option [value]="null">No rejection code selected</mat-option>
            @for (code of rejectionCodeOptions; track code) {
              <mat-option [value]="code">{{ code | expertRejectionCode }}</mat-option>
            }
          </mat-select>
          @if (form.controls.rejectionCode.hasError('required')) {
            <mat-error>Rejection code is required</mat-error>
          }
        </mat-form-field>
      }
    </div>
  </form>
</ng-template>
